import ImageLightboxGallery, { type LightboxItem } from '@/components/media/ImageLightboxGallery'
import { createLightboxPreviewItem } from '@/components/media/ImageLightboxPreviewTrigger'
import ImageUploader from '@/components/media/ImageUploader'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { ActionSurface } from '@/components/patterns/action-surface'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import { DatePickerControl } from '@/components/form/DatePicker'
import { Button } from '@/components/ui/button'
import {
  DetailCard,
  DetailIconBadge,
  detailSurfaceActionClass,
  getDetailWellClass,
} from '@/components/patterns/detail-page'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { NotificationIntentType } from '@/lib/enums/NotificationIntentType'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import { cn } from '@/lib/utils'
import {
  CalendarClock,
  Camera,
  ImagePlus,
  PackageCheck,
  Pencil,
  Send,
  Trash2,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { type ComponentProps, useRef, useState } from 'react'
import type { AuthUserMedia } from '@/lib/services/auth/auth.types'
import type { MediaFile } from '@/lib/services/media/media.types'
import type {
  SalesOrder,
  SalesOrderArrangementPhoto,
  SalesOrderArrangementPhotoSendMode,
} from '@/lib/services/sales-orders/sales-orders.types'

export const ARRANGEMENT_PHOTO_SEND_MODE = {
  sendNow: 'send_now',
  scheduled: 'scheduled',
  deliveryNotification: 'delivery_notification',
  readyForPickupNotification: 'ready_for_pickup_notification',
} as const satisfies Record<string, SalesOrderArrangementPhotoSendMode>

export const ARRANGEMENT_PHOTO_CONTACT_ERROR =
  'Add a purchaser email or phone number before sending this photo.'

const CANCELABLE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES: readonly string[] = [
  'queued',
  'retrying',
  'scheduled',
]
const ACTIVE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES: readonly string[] = [
  ...CANCELABLE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES,
  'sending',
]
const MUTABLE_ARRANGEMENT_PHOTO_NOTIFICATION_TYPES: readonly string[] = [
  'customer_arrangement_photo_email',
  'customer_arrangement_photo_text',
]
const SEND_MORE_READY_ARRANGEMENT_PHOTO_SEND_MODES: readonly SalesOrderArrangementPhotoSendMode[] =
  [
    ARRANGEMENT_PHOTO_SEND_MODE.sendNow,
    ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification,
    ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification,
  ]
const EDITABLE_ARRANGEMENT_PHOTO_SEND_MODES: readonly SalesOrderArrangementPhotoSendMode[] = [
  ARRANGEMENT_PHOTO_SEND_MODE.sendNow,
  ARRANGEMENT_PHOTO_SEND_MODE.scheduled,
  ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification,
  ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification,
]
const NOTIFICATION_SEND_MODES: readonly SalesOrderArrangementPhotoSendMode[] = [
  ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification,
  ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification,
]

export const ARRANGEMENT_PHOTO_NOTIFICATION_INVALIDATE_INTERVAL_MS = 30_000
export const EMPTY_ARRANGEMENT_PHOTOS: readonly SalesOrderArrangementPhoto[] = []
export const MAX_ARRANGEMENT_PHOTO_UPLOADS = 6

function hasActivePhotoSwipeLightbox() {
  return typeof document !== 'undefined' && Boolean(document.querySelector('.pswp'))
}

export function defaultArrangementPhotoScheduledDate(): string {
  return dayjs().add(1, 'day').format('YYYY-MM-DD')
}

function getArrangementPhotoNotificationIntentStatus(photo: SalesOrderArrangementPhoto) {
  return String(photo.notification_intent_status ?? '').toLowerCase()
}

function getArrangementPhotoNotificationIntentType(photo: SalesOrderArrangementPhoto) {
  return String(photo.notification_intent_type ?? '').toLowerCase()
}

export function isArrangementPhotoSent(photo: SalesOrderArrangementPhoto): boolean {
  return Boolean(getArrangementPhotoSentTimestamp(photo))
}

export function isArrangementPhotoSendMoreReady(photo: SalesOrderArrangementPhoto): boolean {
  if (isArrangementPhotoSent(photo)) {
    return true
  }

  if (isArrangementPhotoNotificationInactive(photo)) {
    return false
  }

  if (!photo.notification_queued_at) {
    return false
  }

  return SEND_MORE_READY_ARRANGEMENT_PHOTO_SEND_MODES.includes(photo.send_mode)
}

function isArrangementPhotoNotificationInactive(photo: SalesOrderArrangementPhoto) {
  return ['bounced', 'canceled', 'failure', 'failed'].includes(
    getArrangementPhotoNotificationIntentStatus(photo)
  )
}

export function hasSendMoreReadyArrangementPhotoNotification(
  photos: readonly SalesOrderArrangementPhoto[] = []
): boolean {
  return photos.some((photo) => isArrangementPhotoSendMoreReady(photo))
}

export function hasSentArrangementPhotoNotification(
  photos: readonly SalesOrderArrangementPhoto[] = []
): boolean {
  return photos.some((photo) => isArrangementPhotoSent(photo))
}

export function hasActiveArrangementPhotoNotification(
  photos: readonly SalesOrderArrangementPhoto[] = []
): boolean {
  for (const photo of photos) {
    if (isArrangementPhotoSent(photo) || isArrangementPhotoNotificationInactive(photo)) {
      continue
    }

    const notificationIntentStatus = getArrangementPhotoNotificationIntentStatus(photo)
    const hasQueuedTimestamp = Boolean(photo.notification_queued_at)

    if (
      !hasQueuedTimestamp &&
      !ACTIVE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES.includes(notificationIntentStatus)
    ) {
      continue
    }

    return true
  }

  return false
}

function canMutateArrangementPhotoNotification(photo: SalesOrderArrangementPhoto) {
  if (isArrangementPhotoSent(photo)) {
    return true
  }

  if (isArrangementPhotoNotificationInactive(photo)) {
    return true
  }

  if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.sendNow) {
    return false
  }

  const notificationIntentType = getArrangementPhotoNotificationIntentType(photo)

  if (
    notificationIntentType &&
    !MUTABLE_ARRANGEMENT_PHOTO_NOTIFICATION_TYPES.includes(notificationIntentType)
  ) {
    return false
  }

  const notificationIntentStatus = getArrangementPhotoNotificationIntentStatus(photo)

  return (
    !notificationIntentStatus ||
    CANCELABLE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES.includes(notificationIntentStatus)
  )
}

export function canMutateArrangementPhotoNotifications(
  photos: readonly SalesOrderArrangementPhoto[] = []
): boolean {
  return photos.every((photo) => canMutateArrangementPhotoNotification(photo))
}

export function canSendMoreAfterArrangementPhotoNotifications(
  photos: readonly SalesOrderArrangementPhoto[] = []
): boolean {
  return photos.some((photo) => isArrangementPhotoSendMoreReady(photo))
}

function getArrangementPhotoSentTimestamp(photo: SalesOrderArrangementPhoto) {
  const timestamp =
    photo.sent_at ?? photo.notification_sent_at ?? photo.delivery_notification_included_at ?? null

  if (!timestamp) {
    return null
  }

  try {
    return dayjs(timestamp).isValid() ? timestamp : null
  } catch {
    return null
  }
}

export function getArrangementPhotoMediaItems(
  photos: readonly SalesOrderArrangementPhoto[] = []
): AuthUserMedia[] {
  return photos.flatMap((photo) => (photo.media ? [photo.media] : []))
}

function getArrangementPhotoSortTimestamp(photo: SalesOrderArrangementPhoto) {
  return (
    getArrangementPhotoSentTimestamp(photo) ??
    photo.scheduled_send_at ??
    photo.notification_queued_at ??
    photo.created_at ??
    ''
  )
}

function getTimestampSortValue(timestamp: string) {
  const sortValue = timestamp ? dayjs(timestamp).valueOf() : Number.MAX_SAFE_INTEGER

  return Number.isFinite(sortValue) ? sortValue : Number.MAX_SAFE_INTEGER
}

function getArrangementPhotoSortValue(photo: SalesOrderArrangementPhoto) {
  return getTimestampSortValue(getArrangementPhotoSortTimestamp(photo))
}

interface ArrangementPhotoHistoryGroup {
  key: string
  photos: SalesOrderArrangementPhoto[]
  isSent: true
  sortTimestamp: string
}

interface ArrangementPhotoGroup {
  key: string
  photos: SalesOrderArrangementPhoto[]
  isSent: boolean
  isUpdate: boolean
}

function getArrangementPhotoHistoryGroup(photo: SalesOrderArrangementPhoto) {
  const sentTimestamp = getArrangementPhotoSentTimestamp(photo)

  if (sentTimestamp) {
    return {
      key: `sent-${photo.destination_channel ?? 'email'}-${sentTimestamp}`,
      sortTimestamp: sentTimestamp,
    }
  }

  if (!isArrangementPhotoSendMoreReady(photo)) {
    return null
  }

  const sortTimestamp = photo.notification_queued_at ?? photo.created_at ?? ''

  return {
    key: `queued-${photo.notification_intent_id ?? photo.send_mode}-${sortTimestamp}`,
    sortTimestamp,
  }
}

interface ArrangementPhotoSendModeOptionProps {
  checked: boolean
  icon: LucideIcon
  label: string
  onChange: () => void
}

function ArrangementPhotoSendModeOption({
  checked,
  icon: Icon,
  label,
  onChange,
}: ArrangementPhotoSendModeOptionProps) {
  return (
    <label
      className={cn(
        'group flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm font-medium transition-[border-color,background-color,box-shadow,color] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/25',
        checked
          ? 'border-selection-border bg-accentSecondary text-accentSecondary-foreground ring-1 ring-primary/15'
          : 'border-border bg-card text-foreground hover:border-primary/35 hover:bg-muted/45'
      )}
    >
      <input
        type="radio"
        name="arrangement-photo-send-mode"
        aria-label={label}
        className="sr-only"
        checked={checked}
        onChange={onChange}
      />
      <span
        aria-hidden="true"
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-lg border transition-colors',
          checked
            ? 'border-action bg-action text-action-foreground'
            : 'border-border bg-muted/60 text-muted-foreground group-hover:border-primary/25 group-hover:text-foreground'
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1 leading-tight">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'grid size-4 shrink-0 place-items-center rounded-full border transition-colors',
          checked ? 'border-action bg-action' : 'border-muted-foreground/40 bg-card'
        )}
      >
        {checked ? <span className="size-1.5 rounded-full bg-action-foreground" /> : null}
      </span>
    </label>
  )
}

function getArrangementPhotoGroups(
  photos: readonly SalesOrderArrangementPhoto[] = []
): ArrangementPhotoGroup[] {
  const sortedPhotos = [...photos].sort((firstPhoto, secondPhoto) => {
    const firstTimestamp = getArrangementPhotoSortValue(firstPhoto)
    const secondTimestamp = getArrangementPhotoSortValue(secondPhoto)

    if (firstTimestamp !== secondTimestamp) {
      return firstTimestamp - secondTimestamp
    }

    return firstPhoto.id - secondPhoto.id
  })
  const historyGroupsByKey = new Map<string, ArrangementPhotoHistoryGroup>()
  const unsentPhotos: SalesOrderArrangementPhoto[] = []

  sortedPhotos.forEach((photo) => {
    const historyGroup = getArrangementPhotoHistoryGroup(photo)

    if (!historyGroup) {
      unsentPhotos.push(photo)
      return
    }

    const group: ArrangementPhotoHistoryGroup = historyGroupsByKey.get(historyGroup.key) ?? {
      key: historyGroup.key,
      photos: [],
      isSent: true,
      sortTimestamp: historyGroup.sortTimestamp,
    }

    group.photos.push(photo)
    historyGroupsByKey.set(historyGroup.key, group)
  })

  const historyGroups = Array.from(historyGroupsByKey.values()).sort((firstGroup, secondGroup) => {
    const firstTimestamp = getTimestampSortValue(firstGroup.sortTimestamp)
    const secondTimestamp = getTimestampSortValue(secondGroup.sortTimestamp)

    if (firstTimestamp !== secondTimestamp) {
      return firstTimestamp - secondTimestamp
    }

    return (firstGroup.photos[0]?.id ?? 0) - (secondGroup.photos[0]?.id ?? 0)
  })

  return [
    ...historyGroups.map((group, index) => ({
      ...group,
      isUpdate: index > 0,
    })),
    ...(unsentPhotos.length
      ? [
          {
            key: historyGroups.length ? 'pending-update' : 'pending',
            photos: unsentPhotos,
            isSent: false,
            isUpdate: historyGroups.length > 0,
          },
        ]
      : []),
  ]
}

function isPendingScheduledArrangementPhoto(photo: SalesOrderArrangementPhoto) {
  const notificationIntentStatus = getArrangementPhotoNotificationIntentStatus(photo)

  return (
    photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled &&
    Boolean(photo.scheduled_send_at) &&
    !isArrangementPhotoSent(photo) &&
    ACTIVE_ARRANGEMENT_PHOTO_NOTIFICATION_STATUSES.includes(notificationIntentStatus)
  )
}

export function getPendingScheduledArrangementPhoto(
  photos: readonly SalesOrderArrangementPhoto[] = []
): SalesOrderArrangementPhoto | null {
  return photos.find((photo) => isPendingScheduledArrangementPhoto(photo)) ?? null
}

export function getEditableArrangementPhotoSendMode(
  photo: SalesOrderArrangementPhoto | null | undefined
): SalesOrderArrangementPhotoSendMode {
  if (photo && EDITABLE_ARRANGEMENT_PHOTO_SEND_MODES.includes(photo.send_mode)) {
    return photo.send_mode
  }

  return ARRANGEMENT_PHOTO_SEND_MODE.sendNow
}

export function arrangementPhotoSendModeNeedsCustomerDestination(
  sendMode: SalesOrderArrangementPhotoSendMode
): boolean {
  return !NOTIFICATION_SEND_MODES.includes(sendMode)
}

export function hasArrangementPhotoCustomerDestination(
  order: Pick<SalesOrder, 'purchaser' | 'ordering_contact' | 'purchaser_phone'>
): boolean {
  return Boolean(
    String(order.purchaser?.email ?? '').trim() ||
    String(order.purchaser?.phone ?? '').trim() ||
    String(order.ordering_contact?.phone ?? '').trim() ||
    String(order.purchaser_phone ?? '').trim()
  )
}

function getReadyForPickupArrangementPhotoStatus(
  photo: SalesOrderArrangementPhoto,
  channelLabel: string,
  { isUpdate = false, verb = 'Sent' }: { isUpdate?: boolean; verb?: 'Sent' | 'Sending' } = {}
) {
  const notificationIntentType = photo.notification_intent_type
  const notificationIntentPickupAction = String(photo.notification_intent_pickup_action ?? '')
    .trim()
    .toLowerCase()
  const pickupAction =
    notificationIntentType === NotificationIntentType.CustomerOrderPickedUpEmail.Value ||
    notificationIntentPickupAction === 'picked_up'
      ? 'picked up'
      : 'ready for pickup'

  return `${isUpdate ? 'Update ' : ''}${isUpdate ? verb.toLowerCase() : verb} with ${pickupAction} ${channelLabel}`
}

function getArrangementPhotoStatus(
  photo: SalesOrderArrangementPhoto,
  {
    isFulfilled = false,
    isReadyForPickup = false,
    isUpdate = false,
  }: { isFulfilled?: boolean; isReadyForPickup?: boolean; isUpdate?: boolean } = {}
) {
  const channelLabel = photo.destination_channel === 'text' ? 'text' : 'email'
  const updatePrefix = isUpdate ? 'Update ' : ''
  const isSent = isArrangementPhotoSent(photo)
  const isTriggered = isReadyForPickup || isFulfilled
  const hasStartedSending =
    Boolean(photo.notification_queued_at) ||
    ['sending', 'retrying'].includes(getArrangementPhotoNotificationIntentStatus(photo))

  if (isSent) {
    if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification) {
      return `${updatePrefix}${isUpdate ? 'sent' : 'Sent'} with delivery ${channelLabel}`
    }

    if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification) {
      return getReadyForPickupArrangementPhotoStatus(photo, channelLabel, { isUpdate })
    }

    return `${updatePrefix}${isUpdate ? 'sent' : 'Sent'} by ${channelLabel}`
  }

  if (isArrangementPhotoNotificationInactive(photo)) {
    return isUpdate ? 'Update not sent' : 'Not sent'
  }

  if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled) {
    if (['sending', 'retrying'].includes(getArrangementPhotoNotificationIntentStatus(photo))) {
      return `${updatePrefix}${isUpdate ? 'sending' : 'Sending'} by ${channelLabel}`
    }

    return photo.destination
      ? `${updatePrefix}${isUpdate ? 'scheduled' : 'Scheduled'} for ${channelLabel}`
      : `${updatePrefix}${isUpdate ? 'scheduled' : 'Scheduled'}; needs customer email or phone`
  }

  if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification) {
    if (isFulfilled) {
      return hasStartedSending
        ? `${updatePrefix}${isUpdate ? 'sending' : 'Sending'} with delivery ${channelLabel}`
        : isUpdate
          ? 'Update not sent'
          : 'Not sent'
    }

    return photo.destination
      ? `${updatePrefix}${isUpdate ? 'sends' : 'Sends'} with delivery ${channelLabel}`
      : `${updatePrefix}${isUpdate ? 'sends' : 'Sends'} with delivery notice`
  }

  if (photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification) {
    if (isTriggered) {
      return hasStartedSending
        ? getReadyForPickupArrangementPhotoStatus(photo, channelLabel, {
            isUpdate,
            verb: 'Sending',
          })
        : isUpdate
          ? 'Update not sent'
          : 'Not sent'
    }

    return `${updatePrefix}${isUpdate ? 'sends' : 'Sends'} with ready for pickup ${channelLabel}`
  }

  if (hasStartedSending) {
    return `${updatePrefix}${isUpdate ? 'sending' : 'Sending'} by ${channelLabel}`
  }

  if (isUpdate) {
    return `Update ${channelLabel}`
  }

  return 'Saved'
}

function getArrangementPhotoScheduledTimestamp(photo: SalesOrderArrangementPhoto) {
  return photo.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled ? photo.scheduled_send_at : null
}

function getArrangementPhotoLightboxData(photos: readonly SalesOrderArrangementPhoto[] = []) {
  // Each photo's position in `items`, or null when it has no file to preview.
  const indexes: (number | null)[] = []
  const items: LightboxItem[] = []

  photos.forEach((photo, index) => {
    const item = createLightboxPreviewItem({
      id: photo.id,
      media: photo.media,
      caption: photo.media?.name,
      alt: photo.media?.name || `Arrangement photo ${index + 1}`,
    })

    if (!item) {
      indexes.push(null)
      return
    }

    indexes.push(items.length)
    items.push(item)
  })

  return { indexes, items }
}

interface SalesOrderArrangementPhotosCardProps {
  show: boolean
  order: Pick<SalesOrder, 'fulfillment_type' | 'fulfillment_due_date'>
  arrangementPhotos?: readonly SalesOrderArrangementPhoto[]
  canAddArrangementPhoto: boolean
  canManageArrangementPhotoGallery: boolean
  canSendMoreArrangementPhotos: boolean
  isFulfilled: boolean
  isReadyForPickup: boolean
  formatPhone: (phone: string | null) => string
  onManagePhotos: () => void
  onSendMorePhotos: () => void
}

export function SalesOrderArrangementPhotosCard({
  show,
  order,
  arrangementPhotos = EMPTY_ARRANGEMENT_PHOTOS,
  canAddArrangementPhoto,
  canManageArrangementPhotoGallery,
  canSendMoreArrangementPhotos,
  isFulfilled,
  isReadyForPickup,
  formatPhone,
  onManagePhotos,
  onSendMorePhotos,
}: SalesOrderArrangementPhotosCardProps) {
  if (!show) {
    return null
  }

  const arrangementPhotoGroups = getArrangementPhotoGroups(arrangementPhotos)
  return (
    <DetailCard id="arrangement-photos" accent="arrangementPhotos" contentClassName="space-y-3 p-5">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <DetailIconBadge icon={Camera} tone="brand" />
          <div className="min-w-0">
            <Typography variant="eyebrow">Arrangement photos</Typography>
          </div>
        </div>
      </div>

      {arrangementPhotoGroups.length ? (
        <div className="space-y-3">
          {arrangementPhotoGroups.map((group) => {
            const groupPhotos = group.photos
            const primaryPhoto = groupPhotos[0]

            // Every group holds at least one photo.
            if (!primaryPhoto) {
              return null
            }

            const lightboxData = getArrangementPhotoLightboxData(groupPhotos)
            const sentTimestamp = getArrangementPhotoSentTimestamp(primaryPhoto)
            const scheduledTimestamp = getArrangementPhotoScheduledTimestamp(primaryPhoto)
            const destination =
              primaryPhoto.destination_channel === 'text'
                ? formatPhone(primaryPhoto.destination)
                : primaryPhoto.destination
            const visiblePhotos = groupPhotos.slice(0, 2)
            const hiddenPhotoCount = Math.max(groupPhotos.length - 2, 0)
            const deliveryScheduledDate =
              primaryPhoto.send_mode === ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification &&
              order.fulfillment_due_date &&
              order.fulfillment_type === FulfillmentType.Delivery.Value
                ? dayjs.utc(order.fulfillment_due_date).format('ddd, MMM D')
                : null
            const photoCountLabel = `${groupPhotos.length} ${groupPhotos.length === 1 ? 'photo' : 'photos'}`
            const details = (
              sentTimestamp
                ? [
                    `Sent ${dayjs(sentTimestamp).format('MMM D, YYYY [at] h:mm A')}`,
                    photoCountLabel,
                  ]
                : [
                    groupPhotos.length > 1 ? photoCountLabel : null,
                    deliveryScheduledDate,
                    scheduledTimestamp ? dayjs.utc(scheduledTimestamp).format('ddd, MMM D') : null,
                  ]
            )
              .filter(Boolean)
              .join(' · ')
            const canEditGroup =
              canManageArrangementPhotoGallery && !group.isSent && !group.isUpdate

            return (
              <div key={group.key} className={getDetailWellClass({ tone: 'mint' })}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 gap-3">
                    <ImageLightboxGallery items={lightboxData.items} className="contents">
                      {({ getTriggerProps }) => (
                        <div className="flex h-17 shrink-0 gap-1.5">
                          {visiblePhotos.map((photo, index) => {
                            const previewIndex = lightboxData.indexes[index] ?? null

                            return (
                              <div
                                key={photo.id}
                                className="relative h-17 w-11 overflow-hidden rounded-md bg-muted outline-1 -outline-offset-1 outline-black/5 dark:outline-white/10"
                              >
                                {photo.media?.url && previewIndex !== null ? (
                                  <button
                                    data-raw-button-owner="lightbox-trigger"
                                    {...getTriggerProps(previewIndex, {
                                      mode: 'button',
                                      'aria-label': `Preview arrangement photo ${previewIndex + 1} of ${lightboxData.items.length}`,
                                    })}
                                    className="group/photo block size-full cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
                                  >
                                    <img
                                      src={photo.media.url}
                                      alt={photo.media.name || 'Arrangement photo'}
                                      className="size-full object-cover transition-transform duration-200 group-hover/photo:scale-[1.03]"
                                    />
                                    {index === 1 && hiddenPhotoCount > 0 ? (
                                      <span className="absolute inset-0 flex items-center justify-center bg-background/75 text-xs font-medium text-foreground">
                                        +{hiddenPhotoCount}
                                      </span>
                                    ) : null}
                                  </button>
                                ) : (
                                  <div className="flex size-full items-center justify-center text-muted-foreground">
                                    <ImagePlus className="size-4" />
                                  </div>
                                )}
                                {(!photo.media?.url || previewIndex === null) &&
                                index === 1 &&
                                hiddenPhotoCount > 0 ? (
                                  <div className="absolute inset-0 flex items-center justify-center bg-background/75 text-xs font-medium text-foreground">
                                    +{hiddenPhotoCount}
                                  </div>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </ImageLightboxGallery>
                    <div className="min-w-0 flex-1">
                      <Typography variant="eyebrow">
                        {getArrangementPhotoStatus(primaryPhoto, {
                          isFulfilled,
                          isReadyForPickup,
                          isUpdate: group.isUpdate,
                        })}
                      </Typography>
                      {destination ? (
                        <p className="mt-1 truncate text-sm text-foreground">{destination}</p>
                      ) : (
                        <p className="mt-1 text-sm text-muted-foreground">
                          No customer destination
                        </p>
                      )}
                      {details ? (
                        <Typography variant="label" tone="muted" className="mt-2 block">
                          {details}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                  {canEditGroup ? (
                    <Button
                      variant="outline"
                      size="icon"
                      className={detailSurfaceActionClass}
                      onClick={onManagePhotos}
                      aria-label="Manage arrangement photos"
                    >
                      <Pencil className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            )
          })}
          {canSendMoreArrangementPhotos ? (
            <Button
              type="button"
              variant="outline"
              className="h-10 w-full"
              onClick={onSendMorePhotos}
            >
              <Camera className="size-4" />
              Send more photos
            </Button>
          ) : null}
        </div>
      ) : (
        <div className={getDetailWellClass({ tone: 'mint' })}>
          <div className="flex items-start justify-between gap-3">
            <Typography variant="supporting" className="block min-w-0 flex-1">
              No arrangement photos have been added yet.
            </Typography>
            {canAddArrangementPhoto ? (
              <Button
                variant="outline"
                size="icon"
                className={detailSurfaceActionClass}
                onClick={onManagePhotos}
                aria-label="Add arrangement photo"
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </DetailCard>
  )
}

interface SalesOrderArrangementPhotoDialogProps {
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string
  description: string
  descriptionClassName?: string
  customerUpdateNotice: string | null
  mediaItems: readonly MediaFile[]
  mediaCount: number
  sendMode: SalesOrderArrangementPhotoSendMode
  scheduledDate: string
  error: string | null
  isSendingMoreArrangementPhotos: boolean
  isManagingArrangementPhotoGallery: boolean
  canSaveArrangementPhotoForDelivery: boolean
  canSaveArrangementPhotoForReadyPickup: boolean
  canDeleteGallery?: boolean
  deletePhotoCount?: number
  mutationPending: boolean
  onAddPhotos: () => void
  onRemoveMedia: (mediaId: MediaFile['id']) => void
  onSendModeChange: (sendMode: SalesOrderArrangementPhotoSendMode) => void
  onScheduledDateChange: (date: string) => void
  onDeleteGallery?: () => void | Promise<void>
  onSubmit: () => void | Promise<void>
}

export function SalesOrderArrangementPhotoDialog({
  open,
  onOpenChange,
  title,
  description,
  descriptionClassName,
  customerUpdateNotice,
  mediaItems,
  mediaCount,
  sendMode,
  scheduledDate,
  error,
  isSendingMoreArrangementPhotos,
  isManagingArrangementPhotoGallery,
  canSaveArrangementPhotoForDelivery,
  canSaveArrangementPhotoForReadyPickup,
  canDeleteGallery = false,
  deletePhotoCount = 0,
  mutationPending,
  onAddPhotos,
  onRemoveMedia,
  onSendModeChange,
  onScheduledDateChange,
  onDeleteGallery,
  onSubmit,
}: SalesOrderArrangementPhotoDialogProps) {
  const initialFocusRef = useRef<HTMLDivElement>(null)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const lightboxItems = mediaItems.map((media) =>
    createLightboxPreviewItem({
      media,
      caption: media.name,
      alt: media.name || 'Arrangement photo preview',
    })
  )

  function handleOpenAutoFocus(event: Event) {
    if (mediaCount === 0) {
      return
    }

    event.preventDefault()
    initialFocusRef.current?.focus()
  }

  function handleInteractOutside(event: Event) {
    const target = event.target

    if (
      (isLightboxOpen && hasActivePhotoSwipeLightbox()) ||
      (target instanceof Element && target.closest('.pswp'))
    ) {
      event.preventDefault()
    }
  }

  function handleEscapeKeyDown(event: KeyboardEvent) {
    if (isLightboxOpen && hasActivePhotoSwipeLightbox()) {
      event.preventDefault()
    }
  }

  function handleDialogOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setIsDeleteConfirmOpen(false)
    }

    onOpenChange(isOpen)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          showCloseButton={!mutationPending}
          size="small"
          viewport="contained"
          padding="compact"
          spacing="compact"
          className="grid max-h-[calc(100dvh-1rem)] grid-rows-[auto_minmax(0,1fr)_auto] sm:max-h-[calc(100dvh-2rem)]"
          onOpenAutoFocus={handleOpenAutoFocus}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className={descriptionClassName}>{description}</DialogDescription>
          </DialogHeader>

          <ScrollArea className="-mr-2 min-h-0 pr-3 [&_[data-slot=scroll-area-viewport]]:overscroll-y-contain">
            <div
              ref={initialFocusRef}
              data-testid="arrangement-photo-dialog-initial-focus"
              tabIndex={-1}
              className="space-y-4 py-2 focus:outline-none"
            >
              {customerUpdateNotice ? (
                <div className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
                  <Typography variant="label" tone="interaction" className="block">
                    {customerUpdateNotice}
                  </Typography>
                </div>
              ) : null}

              {mediaCount > 0 ? (
                <div className="space-y-3">
                  <ImageLightboxGallery
                    items={lightboxItems}
                    className="contents"
                    trapFocus={false}
                    returnFocus={false}
                    onOpenChange={setIsLightboxOpen}
                  >
                    {({ getTriggerProps }) => (
                      <div
                        className={cn(
                          'grid gap-3',
                          mediaCount === 1 ? 'grid-cols-1' : 'grid-cols-2'
                        )}
                      >
                        {mediaItems.map((media, index) => (
                          <UploadedMediaPreviewTile
                            key={media.id}
                            media={media}
                            alt={media.name || 'Arrangement photo preview'}
                            caption={media.name}
                            onRemove={() => onRemoveMedia(media.id)}
                            removeLabel="Remove arrangement photo preview"
                            wrapperClassName="min-w-0"
                            frameClassName={cn(
                              'w-full overflow-hidden rounded-xl bg-muted',
                              mediaCount === 1 ? 'aspect-[4/3] max-h-72' : 'aspect-square'
                            )}
                            imageClassName="h-full w-full object-cover"
                            lightboxTriggerProps={getTriggerProps(index, { mode: 'button' })}
                          />
                        ))}
                      </div>
                    )}
                  </ImageLightboxGallery>
                  {mediaCount < MAX_ARRANGEMENT_PHOTO_UPLOADS ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full"
                      onClick={onAddPhotos}
                    >
                      <ImagePlus className="size-4" />
                      Add photos
                    </Button>
                  ) : null}
                </div>
              ) : (
                <ActionSurface
                  type="button"
                  className="h-28 w-full flex-col items-center justify-center gap-2"
                  onClick={onAddPhotos}
                >
                  <ImagePlus className="size-6" />
                  Upload Photos
                </ActionSurface>
              )}

              {error ? (
                <Typography variant="label" tone="destructive" className="block">
                  {error}
                </Typography>
              ) : null}

              {mediaCount > 0 && !isSendingMoreArrangementPhotos ? (
                <div className="space-y-3">
                  <>
                    <Typography variant="label" className="block">
                      When should we send {mediaCount > 1 ? 'these photos' : 'this photo'}?
                    </Typography>
                    <div className="space-y-2">
                      <ArrangementPhotoSendModeOption
                        checked={sendMode === ARRANGEMENT_PHOTO_SEND_MODE.sendNow}
                        icon={Send}
                        label="Send now"
                        onChange={() => onSendModeChange(ARRANGEMENT_PHOTO_SEND_MODE.sendNow)}
                      />
                      <ArrangementPhotoSendModeOption
                        checked={sendMode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled}
                        icon={CalendarClock}
                        label="Send on a scheduled date"
                        onChange={() => onSendModeChange(ARRANGEMENT_PHOTO_SEND_MODE.scheduled)}
                      />
                      {sendMode === ARRANGEMENT_PHOTO_SEND_MODE.scheduled ? (
                        <DatePickerControl
                          label="Send Date"
                          value={scheduledDate}
                          minDate={defaultArrangementPhotoScheduledDate()}
                          format="MM/DD/YYYY"
                          className="w-full"
                          buttonProps={{
                            'aria-label': 'Send Date',
                            id: 'arrangement-photo-scheduled-date',
                          }}
                          onChange={onScheduledDateChange}
                        />
                      ) : null}
                      {canSaveArrangementPhotoForDelivery ? (
                        <ArrangementPhotoSendModeOption
                          checked={sendMode === ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification}
                          icon={Truck}
                          label="Send when order is marked delivered"
                          onChange={() =>
                            onSendModeChange(ARRANGEMENT_PHOTO_SEND_MODE.deliveryNotification)
                          }
                        />
                      ) : null}
                      {canSaveArrangementPhotoForReadyPickup ? (
                        <ArrangementPhotoSendModeOption
                          checked={
                            sendMode === ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification
                          }
                          icon={PackageCheck}
                          label="Send when order is marked ready for pickup"
                          onChange={() =>
                            onSendModeChange(ARRANGEMENT_PHOTO_SEND_MODE.readyForPickupNotification)
                          }
                        />
                      ) : null}
                    </div>
                  </>
                </div>
              ) : null}
            </div>
          </ScrollArea>
          <DialogFooter className="shrink-0 flex-col gap-2 border-t border-border/70 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => handleDialogOpenChange(false)}
                disabled={mutationPending}
              >
                Cancel
              </Button>
              {canDeleteGallery ? (
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  disabled={mutationPending || !onDeleteGallery}
                >
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              ) : null}
            </div>
            <Button onClick={onSubmit} disabled={mutationPending || mediaCount === 0}>
              {mutationPending ? (
                'Saving...'
              ) : (
                <>
                  {isSendingMoreArrangementPhotos ? (
                    <Send aria-hidden="true" className="size-4" />
                  ) : null}
                  {isManagingArrangementPhotoGallery
                    ? 'Save Changes'
                    : isSendingMoreArrangementPhotos
                      ? 'Send'
                      : mediaCount > 1
                        ? 'Send Photos'
                        : 'Send Photo'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        trigger={null}
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        variant="destructive"
        title="Delete Arrangement Photos"
        description={
          deletePhotoCount > 1
            ? 'This cancels the pending send and removes these photos from the order.'
            : 'This cancels the pending send and removes this photo from the order.'
        }
        confirmText="Delete"
        pendingText="Deleting..."
        isPending={mutationPending}
        onConfirm={onDeleteGallery}
      />
    </>
  )
}

type ImageUploaderProps = ComponentProps<typeof ImageUploader>

interface SalesOrderArrangementPhotoUploaderProps {
  orderId: number
  open: boolean
  maxNumberOfFiles: number
  uploadSessionId: number
  onRequestClose: (uploadSessionId: number) => void
  onUploadSuccess: (
    uploadSessionId: number,
    ...upload: Parameters<NonNullable<ImageUploaderProps['onUploadSuccess']>>
  ) => void
  onUploadComplete: (
    uploadSessionId: number,
    ...result: Parameters<NonNullable<ImageUploaderProps['onUploadComplete']>>
  ) => void
  onUploadError: (uploadSessionId: number) => void
}

export function SalesOrderArrangementPhotoUploader({
  orderId,
  open,
  maxNumberOfFiles,
  uploadSessionId,
  onRequestClose,
  onUploadSuccess,
  onUploadComplete,
  onUploadError,
}: SalesOrderArrangementPhotoUploaderProps) {
  return (
    <ImageUploader
      id={`sales-order-arrangement-photo-${orderId}`}
      uploadUsage={MediaUploadUsage.OrderPhoto.Value}
      open={open}
      photoManagerMode={PhotoManagerMode.UploadOnly}
      maxNumberOfFiles={maxNumberOfFiles}
      onRequestClose={() => onRequestClose(uploadSessionId)}
      onUploadSuccess={(file, response) => onUploadSuccess(uploadSessionId, file, response)}
      onUploadComplete={(result) => onUploadComplete(uploadSessionId, result)}
      onUploadError={() => onUploadError(uploadSessionId)}
    />
  )
}
